(function () {
  'use strict';

  const STORAGE_KEY = 'me-group-order-quote-draft';
  const QUOTE_PATH = '/pages/group-order-quote';


  function encodeQuoteDraft(manifest) {
    try {
      const bytes = new TextEncoder().encode(JSON.stringify(manifest));
      let binary = '';
      bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
      return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
    } catch (_) { return ''; }
  }

  function text(root, selector) {
    return String(root.querySelector(selector)?.textContent || '').trim();
  }

  function value(root, selector) {
    return String(root.querySelector(selector)?.value || '').trim();
  }

  function makeReference() {
    const now = new Date();
    const date = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, '0'),
      String(now.getDate()).padStart(2, '0')
    ].join('');
    const random = Math.random().toString(36).slice(2, 7).toUpperCase();
    return `MEQ-${date}-${random}`;
  }

  function getDistribution(designer) {
    return Array.from(designer.querySelectorAll('[data-me-size-quantity]'))
      .map((input) => ({
        size: String(input.dataset.meSizeValue || input.dataset.size || '').trim(),
        quantity: Math.max(0, Number.parseInt(input.value || '0', 10) || 0),
        variantId: String(input.dataset.meVariantId || '')
      }))
      .filter((row) => row.size && row.quantity > 0);
  }

  function getQuantity(designer) {
    const distribution = getDistribution(designer);
    if (distribution.length) {
      return distribution.reduce((sum, row) => sum + row.quantity, 0);
    }
    const quantity = Number.parseInt(value(designer, '[data-me-quantity-input]') || '1', 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  function distributionSummary(distribution) {
    return distribution.map((row) => `${row.size} × ${row.quantity}`).join(', ');
  }

  function getPlacements(designer) {
    return Array.from(
      designer.querySelectorAll('[data-me-placement-checkbox]:checked')
    ).map((input) => input.value);
  }

  function getArtwork() {
    const summary = window.MEArtworkManager?.getSummary?.() || [];
    return summary.map((item) => ({
      placement: item.placement || '',
      type: item.type || '',
      summary: typeof item.summary === 'string'
        ? item.summary
        : JSON.stringify(item.summary || {}),
      fileName: item.fileName || item.name || ''
    }));
  }

  function getNameNumber(designer) {
    return {
      includeName: Boolean(designer.querySelector('[data-me-name-toggle]')?.checked),
      includeNumber: Boolean(designer.querySelector('[data-me-number-toggle]')?.checked),
      name: value(designer, '[data-me-back-name]'),
      number: value(designer, '[data-me-back-number]'),
      font: value(designer, '[data-me-name-number-font]'),
      printSize: value(designer, '[data-me-name-number-size]'),
      textColor: value(designer, '[data-me-name-number-color]')
    };
  }

  function buildManifest(designer) {
    const distribution = getDistribution(designer);
    const quantity = getQuantity(designer);
    const multiSize = distribution.length > 0;
    const selectedSize = multiSize
      ? distributionSummary(distribution)
      : (value(designer, '[data-me-size-property]') || 'Not selected');
    return {
      schemaVersion: '1.1',
      quoteReference: makeReference(),
      createdAt: new Date().toISOString(),
      responseWindow: '24–48 business hours',
      sourceUrl: window.location.href,
      product: {
        title: designer.dataset.meProductTitle || document.title,
        handle: designer.dataset.meProductHandle || '',
        variantId: value(designer, '[data-me-variant-id]'),
        color: text(designer, '[data-me-selected-color]').replace(/^Selected:\s*/i, '') || 'Not selected',
        size: selectedSize,
        quantity,
        orderType: multiSize ? 'Multiple Sizes' : 'Single Size',
        sizeDistribution: distribution
      },
      pricingEstimate: {
        unitPrice: text(designer, '[data-me-price-unit]') || 'Not available',
        estimatedTotal: text(designer, '[data-me-price-total]') || 'Not available',
        discount: 'Custom bulk pricing pending review'
      },
      placements: getPlacements(designer),
      artwork: getArtwork(),
      nameAndNumber: getNameNumber(designer),
      personalizationNotes: String(designer.querySelector('#me-product-notes')?.value || '').trim(),
      proofRequested: Boolean(designer.querySelector('[data-me-proof-checkbox]')?.checked),
      artworkAuthorization: Boolean(designer.querySelector('[data-me-rights-checkbox]')?.checked)
    };
  }

  function setMessage(designer, message) {
    const target = designer.querySelector('[data-me-designer-cart-status]');
    if (target) target.textContent = message;
  }

  function validate(designer) {
    const distribution = getDistribution(designer);
    const size = value(designer, '[data-me-size-property]');
    const quantity = getQuantity(designer);
    const placements = getPlacements(designer);
    const artwork = getArtwork();
    const manager = window.MEArtworkManager;

    if (quantity < 21) return 'Custom apparel bulk quotes begin at 21 shirts. Enter 21 or more pieces before requesting a quote.';
    if (!distribution.length && !size) return 'Select a garment size before requesting a quote.';
    if (!placements.length) return 'Select at least one print location before requesting a quote.';

    if (manager) {
      const missing = placements.filter((placement) => !manager.hasAssignment?.(placement));
      if (missing.length) return `Complete the customization for ${missing[0]} before requesting a quote.`;
    } else if (!artwork.length) {
      return 'Upload the required artwork before requesting a quote.';
    }

    return '';
  }

  function goToQuotePage(designer) {
    const error = validate(designer);
    if (error) {
      setMessage(designer, error);
      const artworkStep = designer.querySelector('[data-me-step-button="artwork"]');
      artworkStep?.click();
      return;
    }

    try {
      const manifest = buildManifest(designer);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(manifest));
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(manifest));
      const draft = encodeQuoteDraft(manifest);
      const draftParam = draft && draft.length < 6500 ? `&draft=${encodeURIComponent(draft)}` : '';
      window.location.assign(`${QUOTE_PATH}?quote=${encodeURIComponent(manifest.quoteReference)}${draftParam}`);
    } catch (error) {
      console.error('Unable to prepare bulk quote handoff.', error);
      setMessage(designer, 'We could not prepare the quote page. Please try again.');
    }
  }

  // v15.4.7.40 — public handoff used by the Studio/cart guard. This keeps
  // 21+ quote requests completely outside Shopify cart mutation code while
  // preserving the same validated quote-page handoff for header and tier CTAs.
  window.MomentsQuoteHandoff = {
    open: goToQuotePage,
    buildManifest
  };

  document.addEventListener('DOMContentLoaded', () => {
    const designer = document.querySelector('[data-me-designer]');
    if (!designer) return;

    designer.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-me-quote-open]');
      if (!opener) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      goToQuotePage(designer);
    }, true);
  });
})();
